import { PATIENT_NARRATIVE_WIZARD_SECTIONS } from "./patientNarrativeWizardSections";

/**
 * Which answers a Patient Narrative wizard section cannot be finished
 * without. Mirrors, key for key, the readiness check the backend runs before
 * generation (POST /patient-narratives/{id}/generation answers 409 with a
 * `missing` list when it fails):
 *
 *   project_conventions -> qParticipantTerm, qTreatmentBlinding, qDateFormat
 *   patient_scope       -> qQualifyingEvents (non-empty), other_events when
 *                          "other" is selected, qParticipantListSource,
 *                          qSubjectIdField and then subject_identifier_confirmed
 *   sources             -> nothing required
 *   narrative_format    -> nothing required
 *
 * Saving never depends on this - the backend stores incomplete sections. It
 * drives the asterisk on a label, the inline "required" messages and
 * disabling Next.
 */
export const REQUIRED_WIZARD_FIELDS = {
  [PATIENT_NARRATIVE_WIZARD_SECTIONS.projectConventions]: [
    "qParticipantTerm",
    "qTreatmentBlinding",
    "qDateFormat",
  ],
  [PATIENT_NARRATIVE_WIZARD_SECTIONS.patientScope]: [
    "qQualifyingEvents",
    "other_events",
    "qParticipantListSource",
    "qSubjectIdField",
    "subject_identifier_confirmed",
  ],
  [PATIENT_NARRATIVE_WIZARD_SECTIONS.sources]: [],
  [PATIENT_NARRATIVE_WIZARD_SECTIONS.narrativeFormat]: [],
};

// Same wording CSR uses for an unfilled required field (see LSForm.js).
export const REQUIRED_FIELD_MESSAGE = "This field is required.";

// "Required" reads wrong for these two: one is a confirmation the user has yet
// to give, the other only exists because "Other" was ticked.
const FIELD_MESSAGES = {
  subject_identifier_confirmed: "Confirm the subject identifier field.",
  other_events: "Describe the other qualifying event(s).",
};

export const requiredFieldMessage = field =>
  FIELD_MESSAGES[field] || REQUIRED_FIELD_MESSAGE;

// Mirrors the backend's notion of "not yet answered": null/undefined, the
// empty string, an empty list, or an unconfirmed (false) confirmation.
export const isAnswered = value => {
  if (value === null || value === undefined || value === "") return false;
  if (Array.isArray(value)) return value.length > 0;
  return value !== false;
};

// Conditional keys: `other_events` is only owed once "other" is selected, and
// `subject_identifier_confirmed` only once there is a field to confirm.
const appliesYet = (field, answers) => {
  if (field === "other_events") {
    return (answers?.qQualifyingEvents || []).includes("other");
  }
  if (field === "subject_identifier_confirmed") {
    return isAnswered(answers?.qSubjectIdField);
  }
  return true;
};

// Whether the label gets an asterisk. `other_events` has no label of its own,
// and `subject_identifier_confirmed` is the Confirm button, so neither is
// treated as a question here.
export const isRequiredField = (section, field) =>
  (REQUIRED_WIZARD_FIELDS[section] || []).includes(field);

export const missingRequiredFields = (section, answers) =>
  (REQUIRED_WIZARD_FIELDS[section] || []).filter(
    field => appliesYet(field, answers) && !isAnswered(answers?.[field])
  );
